/**
 * `/settings/booking` — Tenant booking/scheduling link configuration (L4.4).
 *
 * Tenants set a Calendly URL or custom scheduling link. The link is stored
 * encrypted in the secrets service and auto-appended to outbound SMS templates
 * (instant response, follow-up sequences, missed-call text-back).
 */

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/toast";
import { useBookingLink, useUpdateBookingLink } from "@/hooks/use-settings";

export function SettingsBookingPage() {
  const booking = useBookingLink();
  const update = useUpdateBookingLink();
  const { toast } = useToast();

  const [url, setUrl] = useState("");
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (booking.data && !editing) {
      setUrl(booking.data.booking_url ?? "");
    }
  }, [booking.data, editing]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    try {
      await update.mutateAsync({ booking_url: url.trim() || null });
      toast({ title: "Booking link saved", variant: "success" });
      setEditing(false);
    } catch (err) {
      toast({
        title: "Could not save booking link",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "error",
      });
    }
  }

  async function handleClear() {
    try {
      await update.mutateAsync({ booking_url: null });
      setUrl("");
      toast({ title: "Booking link removed", variant: "success" });
      setEditing(false);
    } catch (err) {
      toast({
        title: "Could not remove booking link",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "error",
      });
    }
  }

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Settings</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Booking Link</h1>
        <p className="text-sm text-muted-foreground">
          Set your scheduling link (Calendly, Cal.com, or custom URL). It will be automatically
          appended to instant response SMS messages and follow-up sequences.
        </p>
      </header>

      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Scheduling URL</CardTitle>
            {booking.data?.booking_url && !editing && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setEditing(true)}
              >
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {booking.isLoading ? (
            <Skeleton className="h-10 w-full rounded-md" />
          ) : booking.data?.booking_url && !editing ? (
            <div className="flex items-center justify-between gap-4">
              <a
                href={booking.data.booking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-mono text-primary hover:underline truncate"
              >
                {booking.data.booking_url}
              </a>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClear}
                disabled={update.isPending}
                className="shrink-0 text-destructive hover:text-destructive"
              >
                Remove
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSave} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="booking-url">URL</Label>
                <Input
                  id="booking-url"
                  type="url"
                  placeholder="https://calendly.com/yourname/30min"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  Paste your Calendly, Cal.com, or any scheduling page URL.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  type="submit"
                  disabled={!url.trim() || update.isPending}
                >
                  {update.isPending ? "Saving…" : "Save"}
                </Button>
                {editing && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setEditing(false);
                      setUrl(booking.data?.booking_url ?? "");
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      <Card className="border-gray-200 bg-gray-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">How this is used</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-xs text-muted-foreground space-y-1.5 list-disc list-inside">
            <li>Appended to instant response SMS: "Want to book a time? [link]"</li>
            <li>Included in missed-call text-back messages</li>
            <li>Available in follow-up sequence templates via <code className="bg-white px-1 py-0.5 rounded border text-xs">{"{{booking_link}}"}</code></li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

export default SettingsBookingPage;
